import { pick } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Person } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import ValidatedFormWithSteps from 'views/Widgets/Forms/ValidatedFormWithSteps';
import Loading from 'views/Widgets/Loading';
import { useWizardConfiguration } from './configuration';
import { useAgentMetadata } from './useAgentMetadata';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  agentId: number;
  onSuccess: (agent: Agent) => void;
}

interface Agent {
  id: number;
  initial_template: string;
  name: string;
  description: string;
  visibility: 'I' | 'P';
  owners: Person[];
  viewers?: unknown;
  instructions?: string;
  associated_documents?: any[];
  associated_skills?: any[];
  behavior: {
    tone_preset?: string;
    challenge_level?: number;
    empathy_balance?: number;
    directness?: number;
    formality?: number;
    proactivity?: number;
  };
  config?: {
    purpose?: string;
    responsibilities?: string;
    guidelines?: string;
    custom_instructions?: string;
  };
}

interface FormObject {
  id: number;
  name: string;
  initial_template: string;
  description: string;
  visibility: 'I' | 'P';
  owners: Person[];
  viewers?: unknown;
  instructions?: string;
  associated_documents?: any[];
  associated_skills?: any[];
  tone_preset?: string;
  challenge_level?: number;
  empathy_balance?: number;
  directness?: number;
  formality?: number;
  proactivity?: number;
  config_purpose?: string;
  config_responsibilities?: string;
  config_guidelines?: string;
  config_custom_instructions?: string;
}

const EditAgentModal: FC<Props> = ({ isOpen, toggle, agentId, onSuccess }) => {
  const { formatMessage } = useIntl();
  const [agent, setAgent] = useState<FormObject | null>(null);

  const { data, status } = useConfirmApi<Agent>({
    url: `agents/${agentId}`,
    method: 'GET',
    disabled: !isOpen,
  });

  const { templates } = useAgentMetadata();
  const template = useMemo(
    () =>
      templates.find(
        (t) => t.id === (agent?.initial_template ?? data?.initial_template)
      ),
    [templates, agent?.initial_template, data?.initial_template]
  );
  const { skills } = useMemo(
    () => ({
      skills: template?.skills || [],
    }),
    [template]
  );

  useEffect(() => {
    if (!agent && data && skills.length > 0) {
      // Transform associated_skills from array of IDs to full skill objects
      const skillObjects = (data.associated_skills || [])
        .map((skillId: string) => skills.find((c) => c.id === skillId))
        .filter(Boolean);

      setAgent({
        ...data,
        ...data.behavior,
        associated_skills: skillObjects,
        config_purpose: data.config?.purpose || '',
        config_responsibilities: data.config?.responsibilities || '',
        config_guidelines: data.config?.guidelines || '',
        config_custom_instructions: data.config?.custom_instructions || '',
      });
    }
  }, [data, skills, agent]);

  const handleCallback = useCallback(
    (responseData: Agent, error: unknown) => {
      if (responseData) {
        toast.success(
          formatMessage({
            id: 'app.views.ai.agents.agents_admin.edit_modal.success',
            defaultMessage: 'Agent saved successfully',
          })
        );
        onSuccess(responseData);
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.ai.agents.agents_admin.edit_modal.error',
            defaultMessage: 'Failed to save agent',
          })
        );
      }
    },
    [formatMessage, onSuccess]
  );

  const transformObjectBeforeSubmit = useCallback(
    (obj: Record<string, unknown>) => {
      return {
        ...obj,
        owners: ((obj.owners ?? []) as Person[]).map(
          (owner: Person) => owner.id
        ),
        viewers: obj.viewers || null,
        associated_documents: (
          (obj.associated_documents ?? []) as Array<{ id: string }>
        ).map((d) => d.id),
        associated_skills: (
          (obj.associated_skills ?? []) as Array<{ id: string }>
        ).map((c) => c.id),
        custom_skills: ((obj.custom_skills ?? []) as Array<{ id: string }>).map(
          (c) => c.id
        ),
        behavior: pick(
          obj,
          'tone_preset',
          'challenge_level',
          'empathy_balance',
          'directness',
          'formality',
          'proactivity'
        ),
        config: {
          purpose: obj.config_purpose || '',
          responsibilities: obj.config_responsibilities || '',
          guidelines: obj.config_guidelines || '',
          custom_instructions: obj.config_custom_instructions || '',
        },
      };
    },
    []
  );

  const handleStepCallback = useCallback(
    (data: unknown, error: unknown) => {
      if (data) {
        const template = templates.find(
          // @ts-expect-error
          (t) => t.id === data.initial_template
        )!;
        setAgent({
          ...data,
          // @ts-expect-error
          ...data.behavior,
          // @ts-expect-error
          associated_skills: (data?.associated_skills ?? []).map(
            (id: string) =>
              template.skills.find((c) => c.id === id) as unknown as any
          ),
        } as FormObject);
      }
      if (error) {
        console.error('Error in step:', error);
      }
    },
    [templates]
  );

  const handleOnValidate = useCallback(
    (obj) => {
      if (obj.visibility === 'C' && !obj.viewers) {
        return {
          viewers: formatMessage({
            id: 'app.views.ai.agents.agents_admin.edit_modal.validation.viewers_required',
            defaultMessage:
              'Please specify a filter when visibility is set to custom.',
          }),
        };
      }
      return {};
    },
    [formatMessage]
  );

  const proxyParameters = useProxyParameters();

  const handleOnSubmit = useCallback(
    (submittedObject, onSubmit) => {
      ConfirmAPI.sendRequestToConfirm(
        agent?.id ? 'PATCH' : 'POST',
        agent?.id ? `/agents/${agent.id}` : '/agents',
        { ...proxyParameters, ...submittedObject },
        (data, error: unknown, hardErrorMessage: unknown) => {
          onSubmit(data, error, hardErrorMessage);
        }
      );
    },
    [proxyParameters, agent?.id]
  );

  const { steps } = useWizardConfiguration({
    toggle,
    template_id: agent?.initial_template ?? '',
    templates,
  });

  if (status === 'LOADING' || !agent) {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.agents_admin.edit_modal.loading',
          defaultMessage: 'Loading agent...',
        })}
      />
    );
  }

  return (
    <>
      {isOpen && (
        <ValidatedFormWithSteps
          object={agent}
          // @ts-expect-error
          steps={steps}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin.edit_modal.title',
            defaultMessage: 'Edit Agent',
          })}
          callback={handleCallback}
          stepCallback={handleStepCallback}
          onValidate={handleOnValidate}
          transformObjectBeforeSubmit={transformObjectBeforeSubmit}
          onCancel={toggle}
          // @ts-expect-error
          submitPromise={handleOnSubmit}
          // renderInputs={renderInputs}
          keepOpenOnSuccess={true}
        />
      )}
    </>
  );
};

export default EditAgentModal;
